import {
  HTTP_INTERCEPTORS,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, effect, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { StartupErrorService } from './../../layout/components/startup-error/startup-error.service';

import { DialogStatus } from 'src/app/core/services/dialog.service';

import { AccountService } from '../api/services/account.service';
import { CustomerDomainsService } from '../api/services/customer-domains.service';
import { LandingPagesService } from '../api/services/landing-pages.service';
import { OrganizationsService } from '../api/services/organizations.service';
import { AuthTokenService } from '../services/authentication/auth-token.service';
import { UserStateService } from '../services/authentication/user-state.service';
import { AppConfigService } from '../services/config.service';
import { DialogService } from '../services/dialog.service';
import { ShowCreditPaymentForPlanService } from '../services/show-credit-payment-for-plan.service';
import { isPublicRoute } from '../utils/util';
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private userSession = inject(UserStateService);
  private accountService = inject(AccountService);
  private appConfigService = inject(AppConfigService);
  private startupErrorService = inject(StartupErrorService);

  //#region properties
  isRefreshTokenInProgress = false;
  tokenRefreshedSource = new Subject();
  tokenSubject = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();
  //#endregion

  constructor() {
    effect(() => {
      this.isRefreshTokenInProgress = this.userSession.isRefreshingToken();
    });
  }

  handleResponseError(error, request?: HttpRequest<any>, next?: HttpHandler) {
    let errMsg = '';
    let errTitle = 'common.error';
    const apiUrl = AppConfigService.config.apiUrl;
    const requestUrl = request.url.replace(apiUrl, '');
    if (!this.appConfigService.configDone) return throwError(() => error);

    // Invalid token error
    if (error.status === 401) {
      if (
        requestUrl ==
        OrganizationsService.OrganizationsGetOrganizationCreditForAdminPath
      ) {
        this.isShowCreditPaymentTypeForPlan();
        return throwError(() => error);
      }
      if (requestUrl == '/Account/RefreshToken') {
        AuthTokenService.clearAllToken();
        return throwError(() => error);
      }

      return this.handle401Error(request, next);
    }

    //Initial request and check server state
    if (
      requestUrl.includes(
        CustomerDomainsService.CustomerDomainsGetCurrentClientDataPath,
      )
    ) {
      this.getServerStateApiUrlApi();
      return throwError(() => error);
    }

    // Access denied error
    else if (error.status === 403) {
      const ignoredPaths = [
        '/Informations/GetInformationsForDashboard',
        '/OutBoxMessages/GetOutBoxMessageDailyStatistics',
        '/OutBoxMessages/GetOutBoxMessageCategorizedDailyStatistics',
        '/Organizations/GetOrganizationCreditForDashboard',
        '/OutBoxMessages/GetScheduledMessageStatisticsByOrganizationId',
        '/Dashboard/GetPishgamDashboard',
        '/Organizations/GetOrganizationCredit',
        '/Users/GetUserCredit',
        '/Organizations/GetOrganizationCreditForAdmin',
        '/Organizations/GetCurrentOrganizationExpireDateForAdmin',
        '/SmartSmsRequests/GetActivePeriodicSmartSmsRequests',
        '/PrivateNumbers/GetCurrentUserPrivateNumbers',
        '/BlessedTombs',
        '/Publishers',
        '/NationalComputerGamesFoundation',
        '/LegalDigitalCulturalInstitutions',
        '/CulturalCenters',
        '/Mosques',
        '/Cinema',
        '/QuranicInstitutes',
        '/Libraries',
        '/Users/GetCurrentUserExpireDate',
        '/Users/GetUserLastLoginInfos',
      ];

      if (requestUrl == '/Organizations/GetOrganizationCreditForAdmin')
        this.isShowCreditPaymentTypeForPlan();

      if (!ignoredPaths.includes(requestUrl)) {
        errTitle = 'messages.htmlStatus.code403';
        errMsg = this.translate.instant('messages.htmlStatus.code403Msg');
        this.openErrorDialog(errTitle, errMsg);
      }
    }

    // NotFound error
    else if (error.status === 404) {
      if (requestUrl.startsWith('/Menus/GetMenuByRoute')) return;
      const ignoredPaths = [
        '/Plans/GetPlan',
        '/SmsPackageDiscounts/VerifyDiscountCode',
        '/PrivateNumbersForBuy/GetLegalOrganizationShahkarInfo',
        '/PrivateNumbersForBuy/GetRealPersonShahkarInfo',
      ];

      if (ignoredPaths.includes(requestUrl)) return throwError(() => error);
      if (requestUrl.startsWith('/Menus/GetMenuByTitle/'))
        return throwError(() => error);

      const landingPageUrl =
        LandingPagesService.LandingPagesGetOrganizationLandingPagePath;
      if (requestUrl.includes(landingPageUrl)) return throwError(() => error);

      errTitle = 'messages.htmlStatus.code404';
      errMsg = error?.error?.message;
      this.dialogService.openDialog({
        title: errTitle,
        message: errMsg,
        status: DialogStatus.error,
      });
    }

    // Method Not Allowed error
    else if (error.status === 405) {
      errTitle = 'messages.htmlStatus.code405';
      errMsg = this.translate.instant('messages.htmlStatus.code405Msg');
      this.openErrorDialog(errTitle, errMsg);
    }

    // Other Error
    else {
      const containsBulkRecipients = error?.error?.validationErrors?.some(
        (item) => item.propertyName.includes('BulkRecipients'),
      );
      if (
        (requestUrl == '/ScheduledMessages/CreateScheduledMessageBulk2' ||
          requestUrl == '/ScheduledMessages/CreateScheduledMessageBulk' ||
          requestUrl ==
            '/ScheduledMessages/GetScheduledMessageCalculationsByBulk2' ||
          requestUrl ==
            '/ScheduledMessages/GetScheduledMessageCalculationsByBulk') &&
        containsBulkRecipients
      ) {
        if (error?.error?.validationErrors) {
          let description = '';
          error.error.validationErrors.forEach((element) => {
            const index = parseInt(
              element?.propertyName?.split('[')[1].split(']')[0],
              10,
            );
            errMsg = `● ${element.message}`;
            description +=
              request.body.bulkRecipients[index].description + '<br>';
          });
          this.openErrorDialog(errMsg, description);
        }
      } else {
        const ignoredPaths = [
          '/CustomerDomains/GetCurrentClientData',
          '/Account/Captcha',
          '/ProformaInvoices/Create',
          '/SmsPackageDiscounts/VerifyDiscountCode',
        ];

        if (ignoredPaths.includes(requestUrl)) return throwError(() => error);

        //form validation Error
        if (error?.error?.validationErrors) {
          error.error.validationErrors.forEach((element) => {
            errMsg += `● ${element.message}<br>`;
          });
          this.openErrorDialog(errTitle, errMsg);
        } else if (error?.error?.message) {
          errTitle = 'common.error';
          errMsg = error.error.message;
          this.openErrorDialog(errTitle, errMsg);
        } else {
          errMsg = this.translate.instant('messages.htmlStatus.code500Msg');
          this.openErrorDialog(errTitle, errMsg);
        }
      }
    }

    return throwError(() => error);
  }

  getServerStateApiUrlApi() {
    const serverStateApiUrl = AppConfigService.config.serverStateApiUrl;
    const serverStateToken = AppConfigService.config.serverStateToken;
    fetch(serverStateApiUrl, {
      headers: { Authorization: serverStateToken },
    })
      .then((res) => res.text())
      .then((status) => {
        if (status == 'normal')
          this.startupErrorService.toggleShowServerError();
        else this.startupErrorService.toggleShowServerUpdate();
      })
      .catch(() => {
        this.startupErrorService.toggleShowServerError();
      });
  }

  isShowCreditPaymentTypeForPlan() {
    ShowCreditPaymentForPlanService.isShowCreditType = false;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshTokenInProgress) {
      this.tokenSubject.next(null);
      this.isRefreshTokenInProgress = true;

      //get old tokens
      const accessToken = AuthTokenService.getAccessToken();
      const refreshToken = AuthTokenService.getRefreshToken();

      return this.accountService
        .accountRefreshToken({
          body: { accessToken: accessToken, refreshToken: refreshToken },
        })
        .pipe(
          switchMap((res) => {
            if (res.isSucceed) {
              this.tokenSubject.next(res);
              AuthTokenService.setAllToken(
                res.data.accessToken,
                res.data.refreshToken,
                AuthTokenService.isRememberMe(),
              );
              return next.handle(
                this.addBearerAndHeaders(request, res.data.accessToken),
              );
            } else this.login(request.url);

            this.isRefreshTokenInProgress = false;
          }),
          catchError((error) => {
            this.isRefreshTokenInProgress = false;
            this.login(request.url);
            return throwError(() => error);
          }),
          finalize(() => {
            this.isRefreshTokenInProgress = false;
          }),
        );
    } else {
      return this.tokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((res) =>
          next.handle(
            this.addBearerAndHeaders(request, (res as any).data.accessToken),
          ),
        ),
      );
    }
  }

  addBearerAndHeaders(req: HttpRequest<any>, token?: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: 'Bearer ' + `${token}` },
    });
  }

  openErrorDialog(
    title?: string,
    message?: string,
    status: DialogStatus = DialogStatus.error,
  ) {
    this.dialogService.openDialog({
      title: title,
      message: message,
      status: status,
    });
  }

  login(reqUrl?) {
    if (reqUrl.includes('/Account/Logout')) {
      AuthTokenService.clearAllToken();
    }

    if (!this.userSession.isShowErr401Dialog) {
      this.userSession.isShowErr401Dialog = true;
      AuthTokenService.clearAllToken();
      this.reloadSite();
    }
  }

  reloadSite() {
    if (!isPublicRoute()) window.location.replace(window.location.origin);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Handle response
    return next.handle(request).pipe(
      catchError((error) => {
        if (request.url.includes('/Account/RefreshToken')) {
          AuthTokenService.clearAllToken();
          this.reloadSite();
        }
        return this.handleResponseError(error, request, next);
      }),
    );
  }
}

export const HttpErrorInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
];
